// src/components/gamification/GamificationBadge.tsx
// Displays gamification tier badge and score for profiles
// Integrates with existing UI without breaking functionality

import React from 'react';
import { motion } from 'framer-motion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface GamificationBadgeProps {
	score?: number;
	tier?: string;
	growthRate?: number;
	isLoading?: boolean;
	size?: 'sm' | 'md' | 'lg';
	showScore?: boolean;
	className?: string;
}

const tierConfig = {
	bronze: {
		color: 'from-orange-600 to-orange-400',
		borderColor: 'border-orange-500',
		icon: '🥉',
		label: 'Bronze',
		minScore: 0,
	},
	silver: {
		color: 'from-gray-400 to-gray-300',
		borderColor: 'border-gray-400',
		icon: '🥈',
		label: 'Silver',
		minScore: 40,
	},
	gold: {
		color: 'from-yellow-500 to-yellow-400',
		borderColor: 'border-yellow-500',
		icon: '🥇',
		label: 'Gold',
		minScore: 60,
	},
	platinum: {
		color: 'from-slate-300 to-slate-200',
		borderColor: 'border-slate-300',
		icon: '💎',
		label: 'Platinum',
		minScore: 75,
	},
	diamond: {
		color: 'from-cyan-400 to-cyan-300',
		borderColor: 'border-cyan-400',
		icon: '💠',
		label: 'Diamond',
		minScore: 90,
	},
};

const sizeConfig = {
	sm: {
		badge: 'h-6 w-6 text-xs',
		container: 'gap-1',
		score: 'text-xs',
		growth: 'text-xs',
	},
	md: {
		badge: 'h-8 w-8 text-sm',
		container: 'gap-2',
		score: 'text-sm',
		growth: 'text-sm',
	},
	lg: {
		badge: 'h-10 w-10 text-lg',
		container: 'gap-3',
		score: 'text-base',
		growth: 'text-base',
	},
};

export function GamificationBadge({
	score = 0,
	tier = 'bronze',
	growthRate = 0,
	isLoading = false,
	size = 'md',
	showScore = true,
	className,
}: GamificationBadgeProps) {
	const config = tierConfig[tier as keyof typeof tierConfig] || tierConfig.bronze;
	const sizes = sizeConfig[size];

	if (isLoading) {
		return (
			<div className={cn('flex items-center', sizes.container, className)}>
				<div className={cn('animate-pulse bg-gray-600 rounded-full', sizes.badge)} />
				{showScore && (
					<div className="flex flex-col gap-1">
						<div className="h-3 w-12 bg-gray-600 rounded animate-pulse" />
						<div className="h-2 w-8 bg-gray-600 rounded animate-pulse" />
					</div>
				)}
			</div>
		);
	}

	// Calculate progress to next tier
	const nextTier = Object.values(tierConfig).find(t => t.minScore > score);
	const progress = nextTier
		? ((score - config.minScore) / (nextTier.minScore - config.minScore)) * 100
		: 100;

	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<motion.div
						className={cn('flex items-center', sizes.container, className)}
						initial={{ opacity: 0, scale: 0.8 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={{ duration: 0.3 }}
					>
						<motion.div
							className={cn(
								'relative rounded-full flex items-center justify-center',
								'bg-gradient-to-br shadow-lg',
								config.color,
								config.borderColor,
								'border-2',
								sizes.badge
							)}
							whileHover={{ scale: 1.1 }}
							whileTap={{ scale: 0.95 }}
						>
							<span className="relative z-10">{config.icon}</span>
							{/* Progress ring */}
							<svg
								className="absolute inset-0 -rotate-90"
								viewBox="0 0 36 36"
							>
								<circle
									cx="18"
									cy="18"
									r="16"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeOpacity="0.2"
								/>
								<circle
									cx="18"
									cy="18"
									r="16"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeDasharray={`${progress} 100`}
									pathLength="100"
									className="transition-all duration-500"
								/>
							</svg>
						</motion.div>

						{showScore && (
							<div className="flex flex-col">
								<span className={cn('font-bold text-foreground', sizes.score)}>
									{score} pts
								</span>
								{growthRate !== 0 && (
									<span
										className={cn(
											'font-medium',
											sizes.growth,
											growthRate > 0 ? 'text-green-400' : 'text-red-400'
										)}
									>
										{growthRate > 0 ? '+' : ''}{growthRate.toFixed(1)}%
									</span>
								)}
							</div>
						)}
					</motion.div>
				</TooltipTrigger>
				<TooltipContent>
					<div className="space-y-2">
						<div className="font-bold">{config.label} Tier</div>
						<div className="text-sm">Score: {score} points</div>
						{growthRate !== 0 && (
							<div className="text-sm">
								Growth: {growthRate > 0 ? '+' : ''}{growthRate.toFixed(1)}%
							</div>
						)}
						{nextTier && (
							<div className="text-sm text-gray-400">
								{nextTier.minScore - score} points to {nextTier.label}
							</div>
						)}
						<div className="w-full bg-gray-700 rounded-full h-2 mt-2">
							<div
								className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
								style={{ width: `${progress}%` }}
							/>
						</div>
					</div>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}